import { Badge, Box, Card, HStack, Stack, Text } from '@chakra-ui/react'
import { LuCircleAlert } from 'react-icons/lu'
import { zeitzielStatus, formatZeitzielIndex, parseNumber } from '../capacity.utils'

function ZeitzielExplanation(label: string): string {
  switch (label) {
    case 'im Zielkorridor':
      return 'Die Durchführung liegt im erwarteten Zeitverlauf.'
    case 'leicht unter Plan':
      return 'Die Durchführung liegt leicht unter dem erwarteten Zeitverlauf.'
    case 'deutlich unter Plan':
      return 'Die Durchführung liegt deutlich unter dem erwarteten Zeitverlauf.'
    case 'leicht über Plan':
      return 'Die Durchführung liegt leicht über dem erwarteten Zeitverlauf.'
    default:
      return 'Die Durchführung liegt deutlich über dem erwarteten Zeitverlauf.'
  }
}

export function ZeitzielBadge({ value }: { value: string }) {
  const pct = parseNumber(value)
  if (!pct)
    return (
      <Text fontSize="sm" color="fg.subtle">
        —
      </Text>
    )
  const status = zeitzielStatus(value)
  const isAlert = status.tone !== 'green'
  return (
    <Badge colorPalette={status.tone} variant="subtle" size="sm" whiteSpace="nowrap">
      {isAlert ? <LuCircleAlert size={12} style={{ marginRight: 4, display: 'inline' }} /> : null}
      {formatZeitzielIndex(value)}
    </Badge>
  )
}

export function ZeitzielIndexCard({ value }: { value: string }) {
  const index = parseNumber(value) / 100
  const status = zeitzielStatus(value)
  const isRed = status.tone === 'red'
  const clamped = Math.min(Math.max(index, 0.8), 1.2)
  const markerLeft = `${((clamped - 0.8) / 0.4) * 100}%`
  const explanation = ZeitzielExplanation(status.label)

  const dotColor = isRed ? 'red.solid' : status.tone === 'yellow' ? 'yellow.solid' : 'gray.solid'

  return (
    <Card.Root variant="subtle" overflow="hidden">
      <Card.Body bg={isRed ? 'red.subtle' : undefined}>
        <Stack gap={3}>
          <HStack justify="space-between" align="center">
            <Text fontSize="sm" fontWeight="semibold" color={isRed ? 'red.fg' : 'fg'}>
              Zeitziel
            </Text>
            <Badge colorPalette={status.tone} variant="subtle" size="sm">
              {status.label}
            </Badge>
          </HStack>
          <Text
            fontSize="2xl"
            fontWeight="semibold"
            color={isRed ? 'red.fg' : 'fg'}
            lineHeight="1"
          >
            {formatZeitzielIndex(value)}
          </Text>
          <Box position="relative" pt={5} pb={1}>
            <Box
              position="absolute"
              left="0"
              right="0"
              top="50%"
              borderTopWidth="1px"
              borderColor={isRed ? 'red.muted' : 'border.emphasized'}
            />
            <Box
              position="absolute"
              left="50%"
              top="calc(50% - 8px)"
              bottom="calc(50% - 8px)"
              borderLeftWidth="2px"
              borderColor={isRed ? 'red.muted' : 'border.emphasized'}
            />
            <Box
              position="absolute"
              top="0"
              left={markerLeft}
              transform="translateX(-50%)"
              w="10px"
              h="10px"
              borderRadius="full"
              bg={dotColor}
              transition="transform 160ms ease, box-shadow 160ms ease"
              _hover={{ transform: 'translateX(-50%) scale(1.15)' }}
            />
            <HStack justify="space-between" fontSize="xs" color={isRed ? 'red.fg' : 'fg'}>
              <Text>0,8</Text>
              <Text>1,0</Text>
              <Text>1,2</Text>
            </HStack>
            <Text mt={2} fontSize="sm" color={isRed ? 'red.fg' : 'fg'}>
              {status.label}
            </Text>
            <Text fontSize="sm" color={isRed ? 'red.fg' : 'fg.subtle'}>
              {explanation}
            </Text>
          </Box>
        </Stack>
      </Card.Body>
    </Card.Root>
  )
}
